/**
 * Octopus Energy Texas plan configuration (DESIGN §3.1) plus engine defaults
 * that are business assumptions (battery, EV, Nest, solar geometry).
 */
import type { BuybackApplyMode, RateEntry, TermMonths } from "./types";

const BUSINESS_2026_10_06 = "Business input, 2026-10-06";
const OCTOPUS_MATH = "docs/research/octopus_math.md";
const VPP_SOURCES =
  "octopusenergy.com + enphase.com (Intelligent Octopus TX), researched 2026-10-06";

export interface OctopusPlanConfig {
  /** Flat energy charge per term, $/kWh. Same rate for every term by default; each is editable. */
  readonly energyRateByTerm: Readonly<Record<TermMonths, RateEntry<number>>>;
  /** Monthly base fee, $/month. */
  readonly baseFee: RateEntry<number>;
  /** Terms offered. */
  readonly terms: RateEntry<readonly TermMonths[]>;
  readonly buyback: {
    /** Solar export credit, $/kWh. */
    readonly rate: RateEntry<number>;
    /** Which charges credits offset (⚠ placeholder until confirmed). */
    readonly applyMode: RateEntry<BuybackApplyMode>;
    /** Whether unused credits roll to the next month (⚠ placeholder until confirmed). */
    readonly rollover: RateEntry<boolean>;
  };
  readonly vpp: {
    /** VPP credit, $ per installed battery kWh per month (linear: kWh / 5 × $20). */
    readonly usdPerKwhMonth: RateEntry<number>;
    /** Backup / grid-event reserve as a fraction of capacity (min SOC). */
    readonly reservePct: RateEntry<number>;
    /** Octopus keeps the credit to fund the free battery, so it is never customer savings. */
    readonly retainedByOctopus: true;
  };
  /** Battery price to the customer, $ (free, funded by VPP). Echoed only; never priced. */
  readonly batteryPriceToCustomer: RateEntry<number>;
}

const energy = (term: TermMonths): RateEntry<number> => ({
  value: 0.08,
  unit: "$/kWh",
  effectiveDate: "2026-10-06",
  source: BUSINESS_2026_10_06,
  note: `${term}-month fixed rate`,
});

export const OCTOPUS_PLAN: OctopusPlanConfig = {
  energyRateByTerm: { 12: energy(12), 18: energy(18), 24: energy(24) },
  baseFee: { value: 0, unit: "$/month", effectiveDate: "2026-10-06", source: OCTOPUS_MATH },
  terms: { value: [12, 18, 24], unit: "months", effectiveDate: "2026-10-06", source: OCTOPUS_MATH },
  buyback: {
    rate: {
      value: 0.08,
      unit: "$/kWh",
      effectiveDate: "2026-10-06",
      source: BUSINESS_2026_10_06,
      note: "1:1 with the energy charge",
    },
    applyMode: {
      value: "energy_only",
      unit: "mode",
      effectiveDate: "2026-10-06",
      source: "Assumption pending Octopus confirmation (DESIGN §7 #1b)",
      placeholder: true,
    },
    rollover: {
      value: true,
      unit: "boolean",
      effectiveDate: "2026-10-06",
      source: "Assumption pending Octopus confirmation (DESIGN §7 #1b)",
      placeholder: true,
    },
  },
  vpp: {
    usdPerKwhMonth: {
      value: 4,
      unit: "$/kWh·month",
      effectiveDate: "2026-10-06",
      source: VPP_SOURCES,
      note: "Linear: 13.5 kWh → $54/month. Retained by Octopus.",
    },
    reservePct: {
      value: 0.2,
      unit: "fraction of capacity",
      effectiveDate: "2026-10-06",
      source: VPP_SOURCES,
    },
    retainedByOctopus: true,
  },
  batteryPriceToCustomer: {
    value: 0,
    unit: "$",
    effectiveDate: "2026-10-06",
    source: BUSINESS_2026_10_06,
    note: "Free battery, funded by the VPP credit",
  },
};

/** Engine defaults that are physical / behavioural assumptions, not prices. */
export interface EngineDefaults {
  readonly battery: {
    readonly capacityKwh: RateEntry<number>;
    readonly powerKw: RateEntry<number>;
    readonly roundTripEfficiency: RateEntry<number>;
    readonly maxDailyCycles: RateEntry<number>;
  };
  readonly ev: {
    readonly kWhPerMile: RateEntry<number>;
    readonly annualMiles: RateEntry<number>;
    /** Smart-charge window [startHour, endHour) in local hour-of-day. */
    readonly chargeWindow: RateEntry<readonly [number, number]>;
  };
  readonly nest: {
    readonly coolingReductionPct: RateEntry<number>;
  };
  readonly solar: {
    readonly tiltDeg: RateEntry<number>;
    readonly azimuthDeg: RateEntry<number>;
  };
}

const ES_DEFAULT = "ES Solar Savings Calculator battery-dispatch defaults";

export const ENGINE_DEFAULTS: EngineDefaults = {
  battery: {
    capacityKwh: { value: 13.5, unit: "kWh", effectiveDate: "2026-10-06", source: "Typical whole-home battery (e.g. 13.5 kWh)" },
    powerKw: { value: 5, unit: "kW", effectiveDate: "2026-10-06", source: ES_DEFAULT },
    roundTripEfficiency: { value: 0.9, unit: "fraction", effectiveDate: "2026-10-06", source: ES_DEFAULT },
    maxDailyCycles: { value: 1, unit: "cycles/day", effectiveDate: "2026-10-06", source: ES_DEFAULT },
  },
  ev: {
    kWhPerMile: { value: 0.3, unit: "kWh/mile", effectiveDate: "2026-10-06", source: "DESIGN §4.1" },
    annualMiles: { value: 12000, unit: "miles/yr", effectiveDate: "2026-10-06", source: "Typical US annual mileage (assumption)" },
    chargeWindow: { value: [0, 5], unit: "hour-of-day [start, end)", effectiveDate: "2026-10-06", source: "DESIGN §4.1 (00:00–05:00)" },
  },
  nest: {
    coolingReductionPct: {
      value: 0.1,
      unit: "fraction of cooling",
      effectiveDate: "2026-10-06",
      source: "Conservative reading of Google Nest's published 10–15% cooling savings (DESIGN §4.1)",
    },
  },
  solar: {
    tiltDeg: { value: 25, unit: "degrees", effectiveDate: "2026-10-06", source: "DESIGN §4.2" },
    azimuthDeg: { value: 180, unit: "degrees (180 = south)", effectiveDate: "2026-10-06", source: "DESIGN §4.2" },
  },
};
